<template>
  <div class="flex" role="tablist">
    <div
      v-for="({ categoryName }, idx) in sizeCategories"
      :key="categoryName"
      role="tab"
      tabindex="0"
      class="flex shrink-0 cursor-pointer items-center whitespace-nowrap border-b px-4 py-2 focus:outline-none focus:ring-2"
      :class="{
        'border-b-white bg-white font-bold': activeTab === idx,
        'border-black-10 bg-black-5': activeTab !== idx,
      }"
      :aria-selected="activeTab === idx"
      @click="selectTab(idx)"
      @keydown.enter.space.prevent="selectTab(idx)"
    >
      {{ $t(`pdp.size_picker.${categoryName}`) }}
      <span
        class="relative ml-1 hidden shrink-0 items-center justify-center align-middle md:inline-flex"
        style="width: 20px; height: 20px; min-width: 20px; min-height: 20px"
      >
        <button
          :id="showIcon(categoryName) ? `infoButton-${idx}` : undefined"
          type="button"
          v-bind="{
            ...(showIcon(categoryName) &&
              !isSmallerThanMd &&
              isTooltipOpened &&
              tooltipTabIndex === idx && {
                'aria-describedby': 'product-size-guide-tooltip',
              }),
          }"
          class="inline-flex size-4 shrink-0 items-center justify-center"
          :class="{ 'pointer-events-none invisible': !showIcon(categoryName) }"
          :aria-label="
            showIcon(categoryName) ? getAriaLabel(categoryName) : undefined
          "
          :aria-hidden="!showIcon(categoryName)"
          @click.stop="
            showIcon(categoryName) && openSizeGuide(categoryName, idx)
          "
        >
          <svg
            class="size-4 shrink-0"
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <g :clip-path="`url(#info-clip-${idx})`" fill="currentColor">
              <path
                d="M8.5 17C3.813 17 0 13.187 0 8.5S3.813 0 8.5 0 17 3.813 17 8.5 13.187 17 8.5 17m0-15.786A7.29 7.29 0 0 0 1.214 8.5 7.29 7.29 0 0 0 8.5 15.786 7.29 7.29 0 0 0 15.786 8.5 7.29 7.29 0 0 0 8.5 1.214"
              />
              <path
                d="M8.5 6.132a.91.91 0 1 0 0-1.821.91.91 0 0 0 0 1.821m0 6.375a.6.6 0 0 1-.607-.607V8.257c0-.34.267-.607.607-.607s.607.267.607.607V11.9c0 .34-.267.607-.607.607"
              />
            </g>
            <defs>
              <clipPath :id="`info-clip-${idx}`">
                <path fill="#fff" d="M0 0h17v17H0z" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <ProductSizeGuideTooltip
          v-if="tooltipTabIndex === idx && isTooltipOpened"
          id="product-size-guide-tooltip"
          ref="sizeGuideTooltip"
          :text="$t(`pdp.size_picker.tooltip.${categoryName}`)"
          :ignore-outside-click-element="`#infoButton-${idx}`"
          class="hidden font-normal md:block"
          @close="closeTooltip"
        />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Variant } from '@scayle/storefront-nuxt'
import type { Size } from '~/utils/product'
import useUiState from '~/composables/ui/useUiState'

const { sizeCategories, hasBothSizeCategories } = defineProps<{
  sizeCategories: { categoryName: string; sizes: Size[] }[]
  hasBothSizeCategories: boolean
  selectedVariant?: Variant
}>()

const { t, te } = useI18n()

const showIcon = (categoryName: string) =>
  hasBothSizeCategories &&
  te(`pdp.size_picker.${categoryName}`) &&
  te(`pdp.size_picker.tooltip.${categoryName}`)

const activeTab = defineModel({ type: Number, default: 0 })
const isTooltipOpened = ref(false)
const tooltipTabIndex = ref<number | null>(null)

const sizeGuideTooltip = shallowRef()

const { smallerOrEqual } = useDefaultBreakpoints()

const isSmallerThanMd = smallerOrEqual('md')

const { openProductSizeGuide: openSizeGuideModal } = useUiState()

const selectTab = (idx: number) => {
  activeTab.value = idx
}

const closeTooltip = () => {
  isTooltipOpened.value = false
  tooltipTabIndex.value = null
}

const openSizeGuide = (categoryName: string, tabIndex: number) => {
  if (isTooltipOpened.value) {
    closeTooltip()
    return
  }

  if (!isSmallerThanMd.value) {
    tooltipTabIndex.value = tabIndex
    isTooltipOpened.value = true
    return
  }

  openSizeGuideModal()
}

const getAriaLabel = (categoryName: string) => {
  const sizeGuideTranslation = t(`pdp.size_picker.${categoryName}`)
  const openSizeGuideTranslation = t('pdp.size_picker.open_size_guide')
  return `${openSizeGuideTranslation} - ${sizeGuideTranslation}`
}
</script>
